const join = (paths: Record<string, unknown>) => Object.values(paths).join('|');

export enum FILES_BASES {
  TODOS = 'todos',
  FILES = 'files',
  ALERTS = 'alerts',
  FILES_HISTORIC = 'archives',
}

export enum TABS_SETTINGS {
  MASTER = `master`,
  SELLERS = `sellers`,
  USERS = `users`,
  TESTS = `tests`,
  IMPORTS = 'imports',
  SPARE_PARTS = 'spare-parts',
}

export enum TABS_CREATE {
  FILES = 'files',
  SPARE_PARTS = 'spare-parts',
}

export enum TABS_CREATE_FILE {
  PRODUCT_REF = 'product-ref',
  ORDER_REF = 'order-ref',
  OWNER = 'owner',
}

export enum TABS_SPARE_PARTS {
  REQUESTS = `request-lines`,
  STOCKS = `stock`,
  STOCK_ITEMS = 'stock-items',
  CREATE_REQUEST = 'create-request',
  REQUESTS_TO_VALIDATE = 'requests-to-validate',
  SUPPLIERS_REQUESTS = 'suppliers-requests',
  DELIVERY_REQUESTS = 'delivery-requests',
  INVENTORIES = 'inventories',
}

export const TABS_TRANSPORT = {
  TO_GROUP: 'to-group',
  ROUTING: 'routing',
} as const;

export type TabsTransport = (typeof TABS_TRANSPORT)[keyof typeof TABS_TRANSPORT];

export enum TABS_INTERVENTION_REPORT {
  INTERVENTION_REPORT_LIST = 'intervention-report-list',
}

export enum TABS_HOME_INTERVENTION {
  INTERVENTION_PLANNING = 'intervention-planning',
  INTERVENTION_TYPE = 'intervention-type',
  INTERVENTION_TECHNICIAN = 'intervention-technician',
  INTERVENTION_ZONES = 'zones',
}

export enum TABS_HISTORIES {
  FILE = `file`,
  ASSIGNMENT = `assignment`,
}

export enum TABS_FILE {
  SUMMARY = 'summary',
  HISTORY = 'history',
  CLIENT = 'client',
  DOCUMENTS = 'documents',
  INVOICES = 'invoices',
  PRODUCT = 'product',
  QUOTATION = 'quotation',
  TRANSPORT = 'transport',
  COMMENTS = 'comments',
  MESSAGING = 'messaging',
  SPARE_PARTS = 'spare-parts',
  TECHNICAL_INTERVENTIONS = 'technical-interventions',
  LOAN = 'loan',
  ACCOUNTING = 'accounting',
  VIDEO_CALL = 'video-call',
}

export enum TABS_FILE_HISTORIC {
  SUMMARY = 'summary',
  PRODUCT = 'product',
  TECHNICAL_INTERVENTIONS = 'technical-interventions',
  COMMENTS = 'comments',
}

export enum TABS_CONFIGURATION_CONSOLE {
  FOLLOW_UP = 'follow-up',
  ENTITIES = 'entities',
  SETTINGS = 'settings',
  OPTIONS = 'options',
  IMPORT = 'import',
}

export enum TABS_BILLING {
  BILLING_TRACKING = 'billing-tracking',
  BILLING_CUSTOMERS_LIST = 'billing-customers-list',
  BILLING_PARAMETERS = 'billing-parameters',
}

export enum TABS_CONSOLE_OPTIONS {
  ENTITIES = 'entities',
  RULES = 'rules',
}

export const NAV = {
  HOME: '',
  WELCOME_PAGE: 'home',
  FROM_SCRATCH: 'from-scratch',
  TODOS: FILES_BASES.TODOS,
  FILES: FILES_BASES.FILES,
  FILES_HISTORIC: FILES_BASES.FILES_HISTORIC,
  BULK_CHANGES: 'bulk-changes',
  ALERTS: FILES_BASES.ALERTS,
  MESSAGES: 'messages',
  ORDERS: 'orders',
  TRANSPORT: 'transport',
  SETTINGS: 'settings',
  DASHBOARD: 'dashboard',
  SPARE_PARTS: 'spare-parts',
  INTERVENTION_REPORT: 'intervention-report',
  BUSINESS_INTELLIGENCE: 'business-intelligence',
  IFRAME: 'iframe',
  HELP: 'help',
  QUOTATION: 'quotation',
  CONFIGURATION_CONSOLE: 'configuration-console',
  DEBUG_INSPECTOR: 'debug-inspector',
  BILLING: 'billing',
  HOME_INTERVENTION: 'home-intervention',
  NEW_CREATE_FILE: 'new-create-file',
  JOBS: 'jobs',
};

export const BASES = {
  HOME: `/${NAV.HOME}`,
  WELCOME_PAGE: `/${NAV.WELCOME_PAGE}`,
  FROM_SCRATCH: `/${NAV.FROM_SCRATCH}`,
  TODOS: `/${NAV.TODOS}`,
  FILES: `/${NAV.FILES}`,
  FILES_HISTORIC: `/${NAV.FILES_HISTORIC}`,
  BULK_CHANGES: `/${NAV.BULK_CHANGES}`,
  ALERTS: `/${NAV.ALERTS}`,
  FILE: `/:fileBase(${join(FILES_BASES)})/:fileId`,
  MESSAGES: `/${NAV.MESSAGES}`,
  ORDERS: `/${NAV.ORDERS}`,
  TRANSPORT: `/${NAV.TRANSPORT}`,
  BUSINESS_INTELLIGENCE: `/${NAV.BUSINESS_INTELLIGENCE}`,
  SETTINGS: `/${NAV.SETTINGS}`,
  DASHBOARD: `/${NAV.DASHBOARD}`,
  SPARE_PARTS: `/${NAV.SPARE_PARTS}`,
  INTERVENTION_REPORT: `/${NAV.INTERVENTION_REPORT}`,
  IFRAME: `/${NAV.IFRAME}`,
  HELP: `/${NAV.HELP}`,
  QUOTATION: `/${NAV.QUOTATION}`,
  CONFIGURATION_CONSOLE: `/${NAV.CONFIGURATION_CONSOLE}`,
  BILLING: `/${NAV.BILLING}`,
  DEBUG_INSPECTOR: `/${NAV.DEBUG_INSPECTOR}`,
  HOME_INTERVENTION: `/${NAV.HOME_INTERVENTION}`,
  NEW_CREATE_FILE: `/${NAV.NEW_CREATE_FILE}`,
  JOBS: `/${NAV.JOBS}`,
};

export const ROUTES = {
  HOME: BASES.HOME,
  SHIPPING_BO: `/shippingBoCallback`,
  WELCOME_PAGE: `${BASES.WELCOME_PAGE}/:unknown?`,
  FROM_SCRATCH: `${BASES.FROM_SCRATCH}`,
  DASHBOARD: BASES.DASHBOARD,
  TODOS: BASES.TODOS,
  BULK_CHANGES: BASES.BULK_CHANGES,
  TODOS_FILE: `${BASES.TODOS}/:fileId`,
  TODOS_FILE_TAB: `${BASES.TODOS}/:fileId/:fileTabId`,
  TODOS_WITH_PARAMS: `${BASES.TODOS}/:fileId?/:fileTabId?/:fileSubTabId?`,
  FILE_DETAILS: `${BASES.FILES}/:fileId/:fileTabId?/:fileSubTabId?`,
  FILES_NEW_UI: `${BASES.FILES}`,
  TODOS_NEW_UI: `${BASES.FILES}/todos`,
  ORDERS_NEW_UI: `${BASES.ORDERS}`,
  TRANSPORT: `${BASES.TRANSPORT}/:transportMenuId?`,
  FILES_HISTORIC: `${BASES.FILES_HISTORIC}/:fileId?/:fileTabId?`,
  ALL_FILES: `${BASES.FILE}?/:fileTabId?`,
  ALERTS: BASES.ALERTS,
  ALERTS_FILE: `${BASES.ALERTS}/:fileId`,
  ALERTS_WITH_PARAMS: `${BASES.ALERTS}/:fileId?/:fileTabId?/:fileSubTabId?`,
  FOLDER_QUOTATION: `${BASES.FILES}/:fileId/quotation`,
  MESSAGES: `${BASES.MESSAGES}/:ticketId?`,
  ORDERS: `${BASES.ORDERS}/:orderId?`,
  ORDER_DETAILS: `${BASES.ORDERS}/:orderId`,
  BUSINESS_INTELLIGENCE: `${BASES.BUSINESS_INTELLIGENCE}`,
  TESTS: `${BASES.SETTINGS}/${TABS_SETTINGS.TESTS}`,
  SPARE_PARTS: `${BASES.SPARE_PARTS}/:sparePartMenuId?`,
  INTERVENTION_REPORT: `${BASES.INTERVENTION_REPORT}/:interventionReportTab?`,
  IFRAME: `${BASES.IFRAME}`,
  HELP: `${BASES.HELP}`,
  ISSUE_A_QUOTATION: `${BASES.QUOTATION}/create-quotation/fromFile/:fileId`,
  SETTINGS: `/settings`,
  ADVANCED_CONFIGURATION_CONSOLE: `/${BASES.SETTINGS}/advancedConfigurationConsole/:consoleTabId?/:routingKey?/:consoleSubTab?/:consoleSubTabId?`,
  BILLING: `/${NAV.BILLING}/:billingTabId?`,
  DEBUG_INSPECTOR: `${BASES.DEBUG_INSPECTOR}/:claimGroupId?`,
  HOME_INTERVENTION: `${BASES.HOME_INTERVENTION}/:homeInterventionTab?`,
  NEW_CREATE_FILE: `${BASES.NEW_CREATE_FILE}`,
  JOBS: '/jobs',
  JOBS_DETAILS: '/jobs/:jobId?',
};

export const ORDERS_RELATIVE_ROUTES = {
  ORDER_DETAILS: `:orderId`,
  ORDERS: `orders`,
};

export const FILES_RELATIVE_ROUTES = {
  FILE_DETAILS_DEFAULT_TAB: `:fileId`,
  FILES_NEW_UI: ``,
  TODOS_NEW_UI: `todos`,
};

export const NEW_CREATE_FILE_ROUTES = {
  BY_CUSTOMER_INFORMATION: 'by-customer-information',
  BY_ORDER_REFERENCE: 'by-order-reference/:orderReference?/:claimGroupId?',
  BY_PRODUCT_REFERENCE: 'by-product-reference',
};

export const IMPORT_MODULE_RELATIVE_ROUTE = 'import-module';

export const BILLING_ROUTES = Object.freeze({
  BILLING_TRACKING: `${BASES.BILLING}/${TABS_BILLING.BILLING_TRACKING}`,
  BILLING_CUSTOMERS_LIST: `${BASES.BILLING}/${TABS_BILLING.BILLING_CUSTOMERS_LIST}`,
  BILLING_PARAMETERS: `${BASES.BILLING}/${TABS_BILLING.BILLING_PARAMETERS}`,
});

export const OPTIONS_CONSOLE_ROUTES = {
  ENTITIES: `entities`,
  RULES: `rules`,
};

export const FILE_ROUTES = {
  CLIENT: `/files/:fileId/${TABS_FILE.CLIENT}`,
  SUMMARY: `/files/:fileId/${TABS_FILE.SUMMARY}`,
  PRODUCT: `/files/:fileId/${TABS_FILE.PRODUCT}`,

  TODO_CLIENT: `${BASES.TODOS}/:fileId/${TABS_FILE.CLIENT}`,
  TODO_SUMMARY: `${BASES.TODOS}/:fileId/${TABS_FILE.SUMMARY}`,
  TODO_PRODUCT: `${BASES.TODOS}/:fileId/${TABS_FILE.PRODUCT}`,

  ALERT_CLIENT: `${BASES.ALERTS}/:fileId/${TABS_FILE.CLIENT}`,
  ALERT_SUMMARY: `${BASES.ALERTS}/:fileId/${TABS_FILE.SUMMARY}`,
  ALERT_PRODUCT: `${BASES.ALERTS}/:fileId/${TABS_FILE.PRODUCT}`,

  COMMENTS: `${BASES.FILE}/${TABS_FILE.COMMENTS}`,
  DEFAULT_TAB: TABS_FILE.SUMMARY,
  DOCUMENTS: `${BASES.FILE}/${TABS_FILE.DOCUMENTS}`,
  HISTORY: `${BASES.FILE}/${TABS_FILE.HISTORY}`,
  INVOICES: `${BASES.FILE}/${TABS_FILE.INVOICES}`,
  QUOTATION: `${BASES.FILE}/${TABS_FILE.QUOTATION}`,
  SPARE_PARTS: `${BASES.FILE}/${TABS_FILE.SPARE_PARTS}`,
  TRANSPORT: `${BASES.FILE}/${TABS_FILE.TRANSPORT}`,
  TECHNICAL_INTERVENTIONS: `${BASES.FILE}/${TABS_FILE.TECHNICAL_INTERVENTIONS}`,
  LOAN: `${BASES.FILE}/${TABS_FILE.LOAN}`,
  ACCOUNTING: `${BASES.FILE}/${TABS_FILE.ACCOUNTING}`,
  VIDEO_CALL: `${BASES.FILE}/${TABS_FILE.VIDEO_CALL}`,
};

export const SPARE_PART_ROUTES = {
  REQUESTS: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.REQUESTS}/:requestLineId?`,
  STOCK_ITEMS: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.STOCK_ITEMS}`,
  CREATE_REQUEST: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.CREATE_REQUEST}`,
  CREATE_REQUEST_FROM_FILE: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.CREATE_REQUEST}/fromFile/:fileId?`,
  REQUESTS_TO_VALIDATE: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.REQUESTS_TO_VALIDATE}/:fileReference?`,
  SUPPLIERS_REQUESTS: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.SUPPLIERS_REQUESTS}`,
  DELIVERY_REQUESTS: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.DELIVERY_REQUESTS}`,
  INVENTORIES: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.INVENTORIES}`,
  INVENTORY_DETAIL: `${BASES.SPARE_PARTS}/${TABS_SPARE_PARTS.INVENTORIES}/:stockInventoryId`,
};

export const TRANSPORT_ROUTES = {
  TO_GROUP: `${BASES.TRANSPORT}/${TABS_TRANSPORT.TO_GROUP}`,
  ROUTING: `${BASES.TRANSPORT}/${TABS_TRANSPORT.ROUTING}`,
};

export const RELATIVE_INTERVENTION_REPORT_ROUTES = {
  INTERVENTION_REPORT_LIST: `${TABS_INTERVENTION_REPORT.INTERVENTION_REPORT_LIST}`,
};

export const RELATIVE_HOME_INTERVENTION_ROUTES = {
  INTERVENTION_PLANNING: `${TABS_HOME_INTERVENTION.INTERVENTION_PLANNING}`,
  INTERVENTION_TYPE: `${TABS_HOME_INTERVENTION.INTERVENTION_TYPE}`,
  INTERVENTION_TECHNICIAN: `${TABS_HOME_INTERVENTION.INTERVENTION_TECHNICIAN}`,
  INTERVENTION_ZONES: `${TABS_HOME_INTERVENTION.INTERVENTION_ZONES}`,
};

export const IFRAME_ROUTES = {
  FILE: TABS_CREATE.FILES,
  SPARE_PART: TABS_CREATE.SPARE_PARTS,
};

export const IFRAME_FILES_ROUTES = {
  ORDER_REF: `${TABS_CREATE_FILE.ORDER_REF}/:orderReference?`,
  OWNER: `${TABS_CREATE_FILE.OWNER}/:ownerId`,
};

export const IFRAME_SPARE_PARTS_ROUTES = {
  CREATE_SPARE_PARTS_REQUEST: `create-request/fromFile/:fileId?`,
};
export const IFRAME_QUOTATION_ROUTES = {
  CREATE_QUOTATION_REQUEST: `quotation/create-quotation/fromFile/:fileId?`,
};

export const IFRAME_INTERVENTION_REPORT_ROUTES = `create-intervention-report/fromFile/:fileId?`;

export const IFRAME_FILE_DOCUMENT_ROUTE = `files/:fileId/documents`;
